import { Button, Checkbox, Col, Input, App, Row } from "antd";

import { CONTENT_WIDTH, GUTTER_SIZE } from "@/enums/";
import { useToggle } from "@/hooks/useToggle";
import { searchFieldType } from "@/propTypes";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bool, exact, func, shape, string } from "prop-types";
import { useEffect, useState } from "react";
import { AdvancedFilterCard } from "./styled";

const { Search } = Input;

const SearchBox = ({
  config: {
    showAdvanced = true,
    advancedSearchFields,
    searchFieldPlaceholder = "Search",
    searchTerm,
    setSearchTerm,
    setFilter,
    isLoading,
    helpText,
    stateKey,
  },
}) => {
  const { modal } = App.useApp();
  const [showAdvancedRow, toggleShowAdvancedRow] = useToggle(false);
  const [fields, setFields] = useState({});

  function showHelp() {
    modal.info({
      title: "Search Tips",
      content: <div dangerouslySetInnerHTML={{ __html: helpText }} />,
      onOk() {
        // do nothing
      },
    });
  }

  const info = (
    <FontAwesomeIcon
      icon={faInfoCircle}
      data-test-id="search-box-show-help"
      onClick={showHelp}
    />
  );

  useEffect(() => {
    if (advancedSearchFields) {
      const newFields = {};

      Object.keys(advancedSearchFields).forEach((key) => {
        newFields[key] = {};
      });

      setFields({
        ...newFields,
        ...fields,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedSearchFields]);

  const setFieldValue = (key, newValue) => {
    if (advancedSearchFields[key].setValue) {
      advancedSearchFields[key].setValue(newValue);
    } else {
      fields[key].value = newValue;
      setFields({ ...fields });
    }
  };

  const applySearch = () => {
    const query = [];
    const filterArr = [];

    Object.keys(advancedSearchFields).forEach((key) => {
      const item = advancedSearchFields[key];
      const value = fields[key].value;

      if (!item.customFilter && value && value.length) {
        query.push(`${item.searchName}:(${value})`);
      }
      if (item.customFilter && item.value) {
        filterArr.push(item.customFilter);
      }
    });

    const final = query.join(" AND ");
    if (final.length) {
      setSearchTerm(final);
    }

    if (filterArr.length) {
      setFilter(filterArr.join(" and "));
    }
  };

  const clearSearchFields = () => {
    Object.keys(fields).map((key) => {
      const item = fields[key];
      item.value = "";
      return item;
    });
    setFields(fields);
  };

  const clearAllSearch = () => {
    clearSearchFields();
    setSearchTerm("");
  };

  const advMap = {};

  if (advancedSearchFields) {
    Object.keys(advancedSearchFields).forEach((key) => {
      const item = advancedSearchFields[key];
      if (!fields[key]) fields[key] = { value: item.value };
      const val = fields[key];

      if (!advMap[item.row || 0]) advMap[item.row || 0] = [];

      if (item.type === "checkbox") {
        advMap[item.row || 0].push(
          <Col span={4} key={`col-${key}`}>
            <Checkbox
              data-test-id={`search-box-checkbox-${key}`}
              key={key}
              defaultChecked={val.value}
              onChange={(e) => {
                setFieldValue(key, e.target.checked);
                applySearch();
              }}
            >
              {item.label}
            </Checkbox>
          </Col>
        );
      } else {
        advMap[item.row || 0].push(
          <Col span={CONTENT_WIDTH.oneSixth} key={`col-${key}`}>
            <span>{item.label}</span>
            <Input
              data-test-id={`search-box-input-${key}`}
              key={key}
              value={val.value}
              onChange={(e) => setFieldValue(key, e.target.value)}
              onPressEnter={applySearch}
            />
          </Col>
        );
      }
    });
  }

  const advancedSearch = Object.keys(advMap).map((key) => (
    <Row key={key} justify="start" gutter={GUTTER_SIZE.large}>
      {advMap[key]}
    </Row>
  ));

  return (
    <>
      <Row className="pb-2">
        <Col
          xxl={CONTENT_WIDTH.oneThird}
          lg={CONTENT_WIDTH.half}
          md={CONTENT_WIDTH.twoThirds}
          xxs={CONTENT_WIDTH.max}
        >
          <Search
            data-test-id={`search-box-search-${stateKey}`}
            key={`${stateKey}-search`}
            placeholder={searchFieldPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={clearSearchFields}
            loading={isLoading}
            suffix={info}
          />
        </Col>
        {!!showAdvanced && (
          <Col>
            <Button
              data-test-id="search-box-toggle-advanced-row"
              onClick={toggleShowAdvancedRow}
            >
              Advanced
            </Button>
          </Col>
        )}
      </Row>
      {!!showAdvanced && !!showAdvancedRow && (
        <div style={{ paddingBottom: "12px" }}>
          <AdvancedFilterCard>{advancedSearch}</AdvancedFilterCard>
          <Row align="bottom" gutter={GUTTER_SIZE.large}>
            <Col md={1}>
              <Button
                data-test-id="search-box-apply-search"
                type="primary"
                onClick={applySearch}
              >
                Search
              </Button>
            </Col>
            <Col md={1}>
              <Button
                data-test-id="search-box-clear-search"
                onClick={clearAllSearch}
                style={{ marginLeft: ".5rem" }}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

SearchBox.propTypes = {
  advancedSearchFields: exact({
    department: searchFieldType,
    jobTitle: searchFieldType,
    role: searchFieldType,
    group: searchFieldType,
    firstName: searchFieldType,
    lastName: searchFieldType,
    manager: searchFieldType,
    viperUser: searchFieldType,
    includeInactiveUsers: searchFieldType,
    includeHiddenUsers: searchFieldType,
  }),
  config: shape({
    showFilter: bool,
    showAdvanced: bool,
    searchFieldPlaceholder: string,
    searchTerm: string.isRequired,
    setSearchTerm: func.isRequired,
    filter: string,
    setFilter: func,
    isLoading: bool,
    helpText: string,
    stateKey: string,
  }),
};

export default SearchBox;
